import ExcelJS from 'exceljs';
import { CompanyData, ValidationResult, ValidationError, CompanyNode, Shareholder } from '../types';

export class ExcelProcessor {
  /**
   * Process an Excel file and convert it to company data
   */
  static async processFile(file: File): Promise<CompanyData[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target?.result as ArrayBuffer;
          const workbook = new ExcelJS.Workbook();
          
          // Load workbook from buffer
          await workbook.xlsx.load(arrayBuffer);
          
          // Get first worksheet
          const worksheet = workbook.worksheets[0];
          
          if (!worksheet) {
            reject(new Error('Excel bestand bevat geen werkbladen.'));
            return;
          }

          // Convert worksheet to array of arrays
          const jsonData: any[][] = [];
          worksheet.eachRow((row, _rowNumber) => {
            const rowData: any[] = [];
            row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
              // Get cell value, handling different types
              let value: any = cell.value;
              if (value !== null && value !== undefined) {
                // Handle formula results, rich text, etc.
                if (typeof value === 'object' && 'result' in value) {
                  value = (value as any).result;
                } else if (typeof value === 'object' && 'richText' in value) {
                  // Extract text from rich text
                  value = (value as any).richText.map((rt: any) => rt.text).join('');
                }
                rowData[colNumber - 1] = value;
              } else {
                rowData[colNumber - 1] = '';
              }
            });
            jsonData.push(rowData);
          });
          
          if (jsonData.length < 2) {
            reject(new Error('Excel bestand moet minimaal een header rij en één data rij bevatten.'));
            return;
          }

          const companyData: CompanyData[] = [];

          // Detect whether the template has a directors column
          // Check header row: if column C (index 2) header contains "director" or "bestuur"
          const headerRow = jsonData[0] || [];
          const headerC = (headerRow[2] || '').toString().toLowerCase();
          const hasDirectorsColumn = headerC.includes('director') || headerC.includes('bestuur');

          // Shareholder pairs start at column index 2 (no directors) or 3 (with directors)
          const shareholderStartCol = hasDirectorsColumn ? 3 : 2;

          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (row.length === 0 || !row[1]) continue; // Skip empty rows

            // Trim country en company name om witruimte-verschillen te
            // voorkomen die anders fantoom-duplicates en mismatched
            // shareholder-references opleveren.
            const company: CompanyData = {
              country: (row[0] ? row[0].toString().trim() : 'Unknown'),
              company: (row[1] ? row[1].toString().trim() : '')
            };

            // Read directors column if present
            if (hasDirectorsColumn && row[2]) {
              const directorsRaw = row[2].toString().trim();
              if (directorsRaw) {
                company.directors = directorsRaw;
              }
            }

            // Add shareholders starting from the appropriate column (shareholder/percentage pairs)
            let shareholderCount = 1;
            for (let j = shareholderStartCol; j < row.length; j += 2) {
              if (row[j] && row[j].toString().trim()) {
                (company as any)[`shareholder${shareholderCount}`] = row[j].toString().trim();
                (company as any)[`percentage${shareholderCount}`] = row[j + 1] || '';
                shareholderCount++;
              }
            }

            companyData.push(company);
          }

          resolve(companyData);
        } catch (error) {
          reject(new Error(`Fout bij het lezen van Excel bestand: ${(error as Error).message}`));
        }
      };

      reader.onerror = () => {
        reject(new Error('Fout bij het lezen van het bestand'));
      };

      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Validate company data before processing
   */
  static validateCompanyData(companyData: CompanyData[]): ValidationResult {
    const errors: ValidationError[] = [];
    const warnings: ValidationError[] = [];
    const companyNames = new Set<string>();
    const shareholderNames = new Set<string>();
    
    // Collect all company names
    companyData.forEach(company => {
      companyNames.add(company.company);
    });
    
    // Collect all shareholder names
    companyData.forEach(company => {
      for (let i = 1; i <= 10; i++) {
        const shareholderKey = `shareholder${i}` as keyof CompanyData;
        const shareholderValue = company[shareholderKey];
        if (shareholderValue && typeof shareholderValue === 'string') {
          shareholderNames.add(shareholderValue);
        }
      }
    });
    
    // Check 1: Root companies (companies without shareholders)
    const rootCompanies = companyData.filter(company => {
      for (let i = 1; i <= 10; i++) {
        const shareholderKey = `shareholder${i}` as keyof CompanyData;
        if (company[shareholderKey]) return false;
      }
      return true;
    });
    
    if (rootCompanies.length === 0) {
      warnings.push({
        type: 'warning',
        title: '⚠️ Geen topbedrijf (root) gevonden',
        message: 'Elk bedrijf in je Excel bestand heeft aandeelhouders. De grafiek toont een gedeelte van de bedrijfsstructuur.',
        solution: `Dit is OK als je alleen een deel van de bedrijfsstructuur wilt tonen. Anders voeg een regel toe zoals:\nLand: NL | Bedrijf: "Ultimate Parent BV" | Shareholder 1: (leeg)`,
        details: `Aantal bedrijven: ${companyData.length}. Bedrijven waarvan aandeelhouders niet in het bestand staan worden als top-niveau bedrijven getoond.`
      });
    }
    
    // Check 2: Missing companies (shareholders that don't exist as companies)
    const missingCompanies: string[] = [];
    const internalShareholders: string[] = [];
    
    shareholderNames.forEach(shareholder => {
      if (companyNames.has(shareholder)) {
        internalShareholders.push(shareholder);
      } else {
        missingCompanies.push(shareholder);
      }
    });
    
    // Filter out obvious external shareholders
    const potentiallyMissing = missingCompanies.filter(name => {
      const lower = name.toLowerCase();
      return !lower.includes('external') && 
             !lower.includes('extern') && 
             !lower.includes('other') && 
             !lower.includes('private') &&
             !lower.includes('management') &&
             name.length > 2;
    });
    
    if (potentiallyMissing.length > 0) {
      const top10 = potentiallyMissing.slice(0, 10);
      const message = potentiallyMissing.length > 10 
        ? `${potentiallyMissing.length} aandeelhouders bestaan niet als bedrijf in kolom B. Dit kan leiden tot "multiple roots" fouten.`
        : `${potentiallyMissing.length} aandeelhouder(s) bestaan niet als bedrijf in kolom B.`;
      
      warnings.push({
        type: 'warning',
        title: '⚠️ Ontbrekende bedrijven gedetecteerd',
        message: message,
        solution: 'Voeg deze bedrijven toe in kolom B, of markeer ze als externe aandeelhouders (bijv. "External Party").',
        details: `Ontbrekende bedrijven${potentiallyMissing.length > 10 ? ' (eerste 10)' : ''}: ${top10.join(', ')}${potentiallyMissing.length > 10 ? `, en ${potentiallyMissing.length - 10} meer...` : ''}`
      });
    }
    
    // Check 3: Name inconsistencies
    const nameIssues: Array<{shareholder: string, possibleMatch: string, issue: string}> = [];
    shareholderNames.forEach(shareholder => {
      if (!companyNames.has(shareholder)) {
        // Check if similar name exists
        const lowerShareholder = shareholder.toLowerCase();
        companyNames.forEach(company => {
          if (company.toLowerCase() === lowerShareholder && company !== shareholder) {
            nameIssues.push({
              shareholder: shareholder,
              possibleMatch: company,
              issue: 'Hoofdletters/kleine letters verschil'
            });
          }
        });
      }
    });
    
    if (nameIssues.length > 0) {
      const top5 = nameIssues.slice(0, 5);
      errors.push({
        type: 'critical',
        title: '❌ Naamgeving inconsistenties gevonden',
        message: `${nameIssues.length} aandeelhouder(s) hebben vergelijkbare maar niet identieke bedrijfsnamen.`,
        solution: 'Zorg dat bedrijfsnamen EXACT overeenkomen (inclusief hoofdletters en speciale tekens). Kopieer/plak namen om fouten te voorkomen.',
        details: top5.map(issue => 
          `"${issue.shareholder}" → mogelijk bedoeld: "${issue.possibleMatch}" (${issue.issue})`
        ).join('\n') + (nameIssues.length > 5 ? `\n... en ${nameIssues.length - 5} meer` : '')
      });
    }
    
    // Check 4: Circular references
    const circularRefs = this.checkForCircularReferences(companyData);
    if (circularRefs.length > 0) {
      errors.push({
        type: 'critical',
        title: '❌ Circulaire referenties gedetecteerd',
        message: 'Sommige bedrijven zijn elkaars aandeelhouder (A bezit B, B bezit A).',
        solution: 'Verwijder circulaire referenties en creëer een duidelijke hiërarchie.',
        details: circularRefs.join('\n')
      });
    }
    
    return { errors, warnings, isValid: errors.length === 0 };
  }

  /**
   * Check for circular references in company data
   */
  private static checkForCircularReferences(companyData: CompanyData[]): string[] {
    const circularRefs: string[] = [];
    const companyMap = new Map<string, string[]>();
    
    // Build company map
    companyData.forEach(company => {
      const shareholders: string[] = [];
      for (let i = 1; i <= 10; i++) {
        const shareholderKey = `shareholder${i}` as keyof CompanyData;
        const shareholderValue = company[shareholderKey];
        if (shareholderValue && typeof shareholderValue === 'string') {
          shareholders.push(shareholderValue);
        }
      }
      companyMap.set(company.company, shareholders);
    });
    
    // Check for circular paths
    const hasCircularPath = (startName: string, currentName: string, visited = new Set<string>()): boolean => {
      if (visited.has(currentName)) {
        return currentName === startName;
      }
      
      visited.add(currentName);
      const shareholders = companyMap.get(currentName) || [];
      
      for (const shareholder of shareholders) {
        if (companyMap.has(shareholder)) {
          if (hasCircularPath(startName, shareholder, new Set(visited))) {
            return true;
          }
        }
      }
      
      return false;
    };
    
    companyData.forEach(company => {
      for (let i = 1; i <= 10; i++) {
        const shareholderKey = `shareholder${i}` as keyof CompanyData;
        const shareholder = company[shareholderKey];
        if (shareholder && typeof shareholder === 'string' && companyMap.has(shareholder)) {
          if (hasCircularPath(company.company, shareholder)) {
            circularRefs.push(`"${company.company}" ↔ "${shareholder}"`);
          }
        }
      }
    });
    
    return Array.from(new Set(circularRefs)); // Remove duplicates
  }

  /**
   * Convert company data to organizational chart nodes
   */
  static processCompanyData(companyData: CompanyData[]): CompanyNode[] {
    const nodes: CompanyNode[] = [];
    const companyMap = new Map<string, {
      name: string;
      country: string;
      directors?: string[];
      shareholders: Shareholder[];
      id: number;
    }>();
    let nodeId = 1;

    // First pass: create all company entries
    companyData.forEach(row => {
      if (!companyMap.has(row.company)) {
        // Parse directors string into array (split by semicolon or comma)
        let directors: string[] | undefined;
        if (row.directors) {
          directors = row.directors.split(/[;,]/).map(d => d.trim()).filter(d => d.length > 0);
        }

        companyMap.set(row.company, {
          name: row.company,
          country: row.country,
          directors,
          shareholders: [],
          id: nodeId++
        });
      }

      // Add shareholders
      for (let i = 1; i <= 10; i++) {
        const shareholderKey = `shareholder${i}` as keyof CompanyData;
        const percentageKey = `percentage${i}` as keyof CompanyData;
        
        const shareholderValue = row[shareholderKey];
        const percentageValue = row[percentageKey];
        
        if (shareholderValue && typeof shareholderValue === 'string' && shareholderValue.trim()) {
          const shareholderName = shareholderValue.trim();
          const percentage = percentageValue && typeof percentageValue === 'string' ? 
            parseFloat(percentageValue) : 100;
          
          // Check if this shareholder is a company in our dataset
          const isCompanyInDataset = companyData.some(r => r.company === shareholderName);
          
          companyMap.get(row.company)!.shareholders.push({
            name: shareholderName,
            percentage: percentage,
            isExternal: !isCompanyInDataset
          });

          // Only add to company map if it's a company in our dataset
          if (isCompanyInDataset && !companyMap.has(shareholderName)) {
            companyMap.set(shareholderName, {
              name: shareholderName,
              country: 'Unknown',
              shareholders: [],
              id: nodeId++
            });
          }
        }
      }
    });

    // Create external shareholder nodes first
    const externalShareholders = new Set<string>();
    companyMap.forEach((company) => {
      company.shareholders.filter(s => s.isExternal).forEach(s => {
        externalShareholders.add(s.name);
      });
    });

    // Add external shareholders as nodes
    externalShareholders.forEach((shareholderName) => {
      const externalId = `ext-${nodeId}`;
      
      nodes.push({
        id: externalId,
        parentId: null,
        name: shareholderName,
        country: 'EXT', // Special country code for external entities
        shareholders: [],
        ownershipInfo: 'External Shareholder',
        position: 'External',
        depth: 0,
        isExpanded: true,
        isHidden: false,
        isVirtualRoot: false
      });
      
      // Add to company map for reference with the full external ID
      companyMap.set(shareholderName, {
        name: shareholderName,
        country: 'EXT',
        shareholders: [],
        id: nodeId // Keep the numeric ID for internal use
      });
      
      nodeId++; // Increment after use
    });

    // Create nodes for org chart
    companyMap.forEach((company) => {
      // Skip external shareholders (already created above)
      if (company.country === 'EXT') return;
      
      // Find the primary parent (largest shareholder) for hierarchy
      let primaryParent: string | null = null;
      let primaryPercentage = 0;
      
      const allShareholders = company.shareholders;
      
      if (allShareholders.length > 0) {
        allShareholders.forEach(shareholder => {
          if (shareholder.percentage > primaryPercentage) {
            primaryParent = shareholder.name;
            primaryPercentage = shareholder.percentage;
          }
        });

        // If all shareholders have the same percentage, use the first one
        if (primaryPercentage === 0) {
          primaryParent = allShareholders[0].name;
          primaryPercentage = allShareholders[0].percentage;
        }
      }

      // Create list of all parent IDs for multiple parent support (including external)
      const parentIds = allShareholders
        .map(s => {
          const parentCompany = companyMap.get(s.name);
          if (parentCompany) {
            // For external shareholders, use the external ID format
            if (parentCompany.country === 'EXT') {
              return `ext-${parentCompany.id}`;
            }
            // For regular companies, use the numeric ID as string
            return parentCompany.id.toString();
          }
          return undefined;
        })
        .filter(id => id !== undefined) as string[];
      

      // Create ownership info string
      let ownershipInfo = '';
      if (company.shareholders.length > 0) {
        ownershipInfo = company.shareholders.map(s => 
          `${s.name}${s.isExternal ? ' (ext.)' : ''}: ${s.percentage}%`
        ).join(', ');
      }

      // Handle primary parent ID (for hierarchy)
      let primaryParentId: string | null = null;
      if (primaryParent) {
        const parentCompany = companyMap.get(primaryParent);
        if (parentCompany) {
          if (parentCompany.country === 'EXT') {
            primaryParentId = `ext-${parentCompany.id}`;
          } else {
            primaryParentId = parentCompany.id.toString();
          }
        }
      }

      nodes.push({
        id: company.id.toString(),
        parentId: primaryParentId,
        parentIds: parentIds.length > 0 ? parentIds : undefined,
        name: company.name,
        country: company.country,
        directors: company.directors,
        shareholders: company.shareholders,
        ownershipInfo: ownershipInfo,
        position: company.country,
        depth: 0, // Will be calculated later
        isExpanded: true, // Default to expanded
        isHidden: false // Default to visible
      });
    });

    // No virtual root - show all companies as separate entities
    let finalNodes = nodes;
    
    // Calculate depths
    return this.calculateNodeDepths(finalNodes);
  }

  /**
   * Calculate depth for each node in the hierarchy
   */
  private static calculateNodeDepths(data: CompanyNode[]): CompanyNode[] {
    const nodeMap = new Map<string, CompanyNode>();
    const depthMap = new Map<string, number>();
    
    // Create a map of all nodes
    data.forEach(node => {
      nodeMap.set(node.id, node);
    });
    
    // Find root nodes (nodes without parents)
    const rootNodes = data.filter(node => !node.parentId);
    
    // Calculate depths using BFS
    const calculateDepthsFromRoot = (rootId: string, startDepth = 0) => {
      const queue: Array<{id: string, depth: number}> = [{id: rootId, depth: startDepth}];
      
      while (queue.length > 0) {
        const {id, depth} = queue.shift()!;
        
        if (!depthMap.has(id) || depthMap.get(id)! > depth) {
          depthMap.set(id, depth);
          
          // Find children of this node
          const children = data.filter(node => node.parentId === id);
          children.forEach(child => {
            queue.push({id: child.id, depth: depth + 1});
          });
        }
      }
    };
    
    // Calculate depths for all roots
    rootNodes.forEach(root => {
      calculateDepthsFromRoot(root.id, 0);
    });
    
    // Add depth property to each node
    return data.map(node => ({
      ...node,
      depth: depthMap.get(node.id) || 0
    }));
  }
}
